/* eslint-disable no-useless-catch */
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, LoginCredentials } from '@/services/auth';
import { TOKEN_KEY } from '@/lib/api';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Origin del panel admin autorizado a embeber este CRM en un iframe e
// inyectar una sesión vía postMessage (ver AccountDetailPage.tsx allá).
const ADMIN_EMBED_ORIGIN = 'https://panel-admin.vendeplay.com';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Verificar autenticación al cargar la app
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Soporte para embed: si estamos dentro de un iframe del admin, avisamos
  // que estamos listos y esperamos que nos inyecte el token por postMessage.
  useEffect(() => {
    if (window === window.parent || !ADMIN_EMBED_ORIGIN) return;

    async function handleMessage(event: MessageEvent) {
      if (event.origin !== ADMIN_EMBED_ORIGIN) return;
      if (event.data?.source !== 'vendeplay-admin-embed' || event.data?.type !== 'auth') return;

      const token = event.data?.token as string | undefined;
      if (!token) return;

      localStorage.setItem(TOKEN_KEY, token);
      await checkAuthentication();
      navigate('/dashboard', { replace: true });
    }

    window.addEventListener('message', handleMessage);
    window.parent.postMessage({ source: 'vendeplay-crm-embed', type: 'ready' }, ADMIN_EMBED_ORIGIN);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const checkAuthentication = async () => {
    try {
      // Verificar si hay token almacenado
      if (!authService.hasToken()) {
        setIsLoading(false);
        return;
      }

      // Primero intentar obtener usuario desde localStorage (más rápido)
      const storedUser = authService.getUserFromStorage();
      if (storedUser) {
        setUser(storedUser);
      }

      // Luego verificar con el servidor para validar el token
      const userData = await authService.checkAuth();
      if (userData) {
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Error durante checkAuth:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);

      if (response.code === 'LOGIN_OK') {
        // El token ya está guardado en localStorage por authService.login
        // Construir objeto User desde la respuesta
        const userData: User = {
          id: response.data.user.accountCode,
          accountCode: response.data.user.accountCode,
          email: response.data.user.email,
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
        };

        setUser(userData);
        navigate('/dashboard');
      } else {
        throw new Error(response.message || 'Error al iniciar sesión');
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error durante logout:', error);
      // Limpiar estado local incluso si falla
      setUser(null);
      navigate('/login');
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
}
